import {AnalyticsTarget, track} from '../../../../../service/analytics/analytics';
import {getEnumValueEnsure} from '../../../../../util/enum';
import {QueryMapType} from '../../../../../util/type';

import {
    AutoAbuseEnum,
    ContentOfReviewsEnum,
    DeletedReviewsEnum,
    HasNotesEnum,
    HiddenReviewsEnum,
    ModeratedEnum,
    PossibilityOfAnAnswerEnum,
    RepliesToReviewsEnum,
    ReplyPublishedEnum,
    ReviewChangedEnum,
    ReviewNameEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
    WaitingForSecondAnswerEnum,
    WithoutTagEnum,
} from './reviews-filter-type';

export function trackAnalytics(filterName: string, value: Array<string | number> | string): void {
    track(
        `${AnalyticsTarget.ReviewsManagement.Reviews.UseFilter} ${filterName.replaceAll(/[_-]/g, ' ')}`,
        Array.isArray(value) ? value.join(', ') : value
    );
}

// eslint-disable-next-line complexity, max-statements,sonarjs/cognitive-complexity
export function getReviewsFilterDataFromForm(form: {
    getFieldValue: (fieldName: string) => unknown;
}): ReviewsFilterDataType {
    const reviewSourceListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.sourceList);
    const sourceList: Array<number> = Array.isArray(reviewSourceListRaw) ? reviewSourceListRaw.map(Number) : [];
    const reviewStatusListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.processStatus);
    const statusList: Array<string> = Array.isArray(reviewStatusListRaw) ? reviewStatusListRaw : [];
    const customReviewStatusListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.customProcessStatus);
    const customStatusList: Array<string> = Array.isArray(customReviewStatusListRaw) ? customReviewStatusListRaw : [];
    const ratingListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.ratingList);
    const ratingList: Array<number> = Array.isArray(ratingListRaw) ? ratingListRaw.map(Number) : [];
    const employeeResponsibleRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.employeesResponsible);
    const employeeResponsible: Array<string> = Array.isArray(employeeResponsibleRaw)
        ? employeeResponsibleRaw.filter(Boolean)
        : [];
    const agencyRepliersRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.agencyRepliers);
    const agencyRepliers: Array<string> = Array.isArray(agencyRepliersRaw) ? agencyRepliersRaw.filter(Boolean) : [];
    const employeeRespondingRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.employeesResponding);
    const employeeResponding: Array<string> = Array.isArray(employeeRespondingRaw)
        ? employeeRespondingRaw.filter(Boolean)
        : [];
    const doctorsRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.doctorNames);
    const doctors: Array<string> = Array.isArray(doctorsRaw) ? doctorsRaw.map(String) : [];
    const tagListRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.tagList);
    const tagList: Array<number> = Array.isArray(tagListRaw)
        ? tagListRaw.flatMap((item) => (typeof item === 'string' ? item.split(', ').map(Number) : item))
        : [];
    const autoReplayRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.autoReplay);
    const autoReplay: Array<string> = Array.isArray(autoReplayRaw) ? autoReplayRaw.map(String) : [];

    const withoutTag: WithoutTagEnum = getEnumValueEnsure<WithoutTagEnum>(
        WithoutTagEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.withoutTag),
        WithoutTagEnum.noSelected
    );
    const hasNotes: HasNotesEnum = getEnumValueEnsure<HasNotesEnum>(
        HasNotesEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.hasNotes),
        HasNotesEnum.noSelected
    );
    const hiddenReviews: HiddenReviewsEnum = getEnumValueEnsure<HiddenReviewsEnum>(
        HiddenReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.hiddenReviews),
        HiddenReviewsEnum.noSelected
    );

    const contentOfReviews: ContentOfReviewsEnum = getEnumValueEnsure<ContentOfReviewsEnum>(
        ContentOfReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.contentOfReviews),
        ContentOfReviewsEnum.noSelected
    );
    const deletedReviews: DeletedReviewsEnum = getEnumValueEnsure<DeletedReviewsEnum>(
        DeletedReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.deletedReviews),
        DeletedReviewsEnum.noSelected
    );
    const repliesToReviews: RepliesToReviewsEnum = getEnumValueEnsure<RepliesToReviewsEnum>(
        RepliesToReviewsEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.repliesToReviews),
        RepliesToReviewsEnum.noSelected
    );
    const possibilityOfAnAnswer: PossibilityOfAnAnswerEnum = getEnumValueEnsure<PossibilityOfAnAnswerEnum>(
        PossibilityOfAnAnswerEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.possibilityOfAnAnswer),
        PossibilityOfAnAnswerEnum.noSelected
    );
    const reviewType: ReviewNameEnum = getEnumValueEnsure<ReviewNameEnum>(
        ReviewNameEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewType),
        ReviewNameEnum.noSelected
    );
    const abuseStatusRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.abuseStatus);
    const abuseStatus: Array<string> = Array.isArray(abuseStatusRaw) ? abuseStatusRaw.map(String) : [];
    const autoAbuseRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.autoAbuse);
    const autoAbuse: AutoAbuseEnum = getEnumValueEnsure<AutoAbuseEnum>(
        AutoAbuseEnum,
        autoAbuseRaw,
        AutoAbuseEnum.notSelected
    );
    const reviewChanged: ReviewChangedEnum = getEnumValueEnsure<ReviewChangedEnum>(
        ReviewChangedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewChanged),
        ReviewChangedEnum.noSelected
    );
    const waitingForSecondAnswer: WaitingForSecondAnswerEnum = getEnumValueEnsure<WaitingForSecondAnswerEnum>(
        WaitingForSecondAnswerEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.waitingForSecondAnswer),
        WaitingForSecondAnswerEnum.noSelected
    );
    const reviewModerated: ModeratedEnum = getEnumValueEnsure<ModeratedEnum>(
        ModeratedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.reviewModerated),
        ModeratedEnum.noSelected
    );
    const replyPublished: ReplyPublishedEnum = getEnumValueEnsure<ReplyPublishedEnum>(
        ReplyPublishedEnum,
        form.getFieldValue(ReviewsFilterFieldNameEnum.replyPublished),
        ReplyPublishedEnum.noSelected
    );

    const dateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.dateFrom);
    const dateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.dateTo);
    const dateFrom: Date | null =
        dateFromRaw && (dateFromRaw as {toISOString?: () => string}).toISOString
            ? new Date((dateFromRaw as {toISOString: () => string}).toISOString())
            : null;
    const dateTo: Date | null =
        dateToRaw && (dateToRaw as {toISOString?: () => string}).toISOString
            ? new Date((dateToRaw as {toISOString: () => string}).toISOString())
            : null;

    const collectionDateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateFrom);
    const collectionDateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateTo);
    const collectionDateFrom: Date | null =
        collectionDateFromRaw && (collectionDateFromRaw as {toISOString?: () => string}).toISOString
            ? new Date((collectionDateFromRaw as {toISOString: () => string}).toISOString())
            : null;
    const collectionDateTo: Date | null =
        collectionDateToRaw && (collectionDateToRaw as {toISOString?: () => string}).toISOString
            ? new Date((collectionDateToRaw as {toISOString: () => string}).toISOString())
            : null;

    const replyDateFromRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.replyDateFrom);
    const replyDateToRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.replyDateTo);
    const replyDateFrom: Date | null =
        replyDateFromRaw && (replyDateFromRaw as {toISOString?: () => string}).toISOString
            ? new Date((replyDateFromRaw as {toISOString: () => string}).toISOString())
            : null;
    const replyDateTo: Date | null =
        replyDateToRaw && (replyDateToRaw as {toISOString?: () => string}).toISOString
            ? new Date((replyDateToRaw as {toISOString: () => string}).toISOString())
            : null;

    const brandIdsRaw = form.getFieldValue(ReviewsFilterFieldNameEnum.brandIds);
    const brandIds: number | null = brandIdsRaw ? Number(brandIdsRaw) : null;

    return {
        [ReviewsFilterFieldNameEnum.brandIds]: brandIds,
        [ReviewsFilterFieldNameEnum.sourceList]: sourceList,
        [ReviewsFilterFieldNameEnum.processStatus]: statusList,
        [ReviewsFilterFieldNameEnum.customProcessStatus]: customStatusList,
        [ReviewsFilterFieldNameEnum.ratingList]: ratingList,
        [ReviewsFilterFieldNameEnum.employeesResponding]: employeeResponding,
        [ReviewsFilterFieldNameEnum.employeesResponsible]: employeeResponsible,
        [ReviewsFilterFieldNameEnum.doctorNames]: doctors,
        [ReviewsFilterFieldNameEnum.tagList]: tagList,
        [ReviewsFilterFieldNameEnum.withoutTag]: withoutTag,
        [ReviewsFilterFieldNameEnum.hasNotes]: hasNotes,
        [ReviewsFilterFieldNameEnum.hiddenReviews]: hiddenReviews,

        [ReviewsFilterFieldNameEnum.autoReplay]: autoReplay,
        [ReviewsFilterFieldNameEnum.contentOfReviews]: contentOfReviews,
        [ReviewsFilterFieldNameEnum.deletedReviews]: deletedReviews,
        [ReviewsFilterFieldNameEnum.repliesToReviews]: repliesToReviews,
        [ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: possibilityOfAnAnswer,
        [ReviewsFilterFieldNameEnum.reviewType]: reviewType,
        [ReviewsFilterFieldNameEnum.abuseStatus]: abuseStatus,
        [ReviewsFilterFieldNameEnum.autoAbuse]: autoAbuse,
        [ReviewsFilterFieldNameEnum.reviewChanged]: reviewChanged,
        [ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: waitingForSecondAnswer,
        [ReviewsFilterFieldNameEnum.reviewModerated]: reviewModerated,
        [ReviewsFilterFieldNameEnum.replyPublished]: replyPublished,

        [ReviewsFilterFieldNameEnum.dateFrom]: dateFrom,
        [ReviewsFilterFieldNameEnum.dateTo]: dateTo,
        [ReviewsFilterFieldNameEnum.collectionDateFrom]: collectionDateFrom,
        [ReviewsFilterFieldNameEnum.collectionDateTo]: collectionDateTo,
        [ReviewsFilterFieldNameEnum.replyDateFrom]: replyDateFrom,
        [ReviewsFilterFieldNameEnum.replyDateTo]: replyDateTo,
        [ReviewsFilterFieldNameEnum.agencyRepliers]: agencyRepliers,
    };
}

// eslint-disable-next-line max-statements, complexity, sonarjs/cognitive-complexity
export function getReviewsFilterDataFromUrlQuery(urlQueries: QueryMapType): ReviewsFilterDataType {
    const reviewSourceListRaw = urlQueries[ReviewsFilterFieldNameEnum.sourceList];
    const sourceList: Array<number> =
        typeof reviewSourceListRaw === 'string' ? reviewSourceListRaw.split(',').map(Number) : [];
    const reviewStatusListRaw = urlQueries[ReviewsFilterFieldNameEnum.processStatus];
    const statusList: Array<string> = typeof reviewStatusListRaw === 'string' ? reviewStatusListRaw.split(',') : [];
    const customReviewStatusListRaw = urlQueries[ReviewsFilterFieldNameEnum.customProcessStatus];
    const customStatusList: Array<string> =
        typeof customReviewStatusListRaw === 'string' ? customReviewStatusListRaw.split(',') : [];
    const ratingListRaw = urlQueries[ReviewsFilterFieldNameEnum.ratingList];
    const ratingList: Array<number> = typeof ratingListRaw === 'string' ? ratingListRaw.split(',').map(Number) : [];
    const employeeResponsibleRaw = urlQueries[ReviewsFilterFieldNameEnum.employeesResponsible];
    const employeeResponsible: Array<string> = employeeResponsibleRaw ? employeeResponsibleRaw.split(',') : [];
    const agencyRepliersRaw = urlQueries[ReviewsFilterFieldNameEnum.agencyRepliers];
    const agencyRepliers: Array<string> = agencyRepliersRaw ? agencyRepliersRaw.split(',') : [];
    const employeeRespondingRaw = urlQueries[ReviewsFilterFieldNameEnum.employeesResponding];
    const employeeResponding: Array<string> = employeeRespondingRaw ? employeeRespondingRaw.split(',') : [];
    const doctorsRaw = urlQueries[ReviewsFilterFieldNameEnum.doctorNames];
    const doctors: Array<string> = doctorsRaw ? doctorsRaw.split(',') : [];
    const tagListRaw = urlQueries[ReviewsFilterFieldNameEnum.tagList];
    const tagList: Array<number> = typeof tagListRaw === 'string' ? tagListRaw.split(',').map(Number) : [];
    const autoReplayRaw = urlQueries[ReviewsFilterFieldNameEnum.autoReplay];
    const autoReplay: Array<string> = autoReplayRaw ? autoReplayRaw.split(',') : [];

    const withoutTag: WithoutTagEnum = getEnumValueEnsure<WithoutTagEnum>(
        WithoutTagEnum,
        urlQueries[ReviewsFilterFieldNameEnum.withoutTag],
        WithoutTagEnum.noSelected
    );
    const hasNotes: HasNotesEnum = getEnumValueEnsure<HasNotesEnum>(
        HasNotesEnum,
        urlQueries[ReviewsFilterFieldNameEnum.hasNotes],
        HasNotesEnum.noSelected
    );
    const hiddenReviews: HiddenReviewsEnum = getEnumValueEnsure<HiddenReviewsEnum>(
        HiddenReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.hiddenReviews],
        HiddenReviewsEnum.noSelected
    );

    const contentOfReviews: ContentOfReviewsEnum = getEnumValueEnsure<ContentOfReviewsEnum>(
        ContentOfReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.contentOfReviews],
        ContentOfReviewsEnum.noSelected
    );
    const deletedReviews: DeletedReviewsEnum = getEnumValueEnsure<DeletedReviewsEnum>(
        DeletedReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.deletedReviews],
        DeletedReviewsEnum.noSelected
    );
    const repliesToReviews: RepliesToReviewsEnum = getEnumValueEnsure<RepliesToReviewsEnum>(
        RepliesToReviewsEnum,
        urlQueries[ReviewsFilterFieldNameEnum.repliesToReviews],
        RepliesToReviewsEnum.noSelected
    );
    const possibilityOfAnAnswer: PossibilityOfAnAnswerEnum = getEnumValueEnsure<PossibilityOfAnAnswerEnum>(
        PossibilityOfAnAnswerEnum,
        urlQueries[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer],
        PossibilityOfAnAnswerEnum.noSelected
    );
    const reviewType: ReviewNameEnum = getEnumValueEnsure<ReviewNameEnum>(
        ReviewNameEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewType],
        ReviewNameEnum.noSelected
    );
    const abuseStatusRaw = urlQueries[ReviewsFilterFieldNameEnum.abuseStatus];
    const abuseStatus: Array<string> = typeof abuseStatusRaw === 'string' ? abuseStatusRaw.split(',') : [];
    const autoAbuseRaw = urlQueries[ReviewsFilterFieldNameEnum.autoAbuse];
    const autoAbuse: AutoAbuseEnum = getEnumValueEnsure<AutoAbuseEnum>(
        AutoAbuseEnum,
        autoAbuseRaw,
        AutoAbuseEnum.notSelected
    );
    const reviewChanged: ReviewChangedEnum = getEnumValueEnsure<ReviewChangedEnum>(
        ReviewChangedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewChanged],
        ReviewChangedEnum.noSelected
    );
    const waitingForSecondAnswer: WaitingForSecondAnswerEnum = getEnumValueEnsure<WaitingForSecondAnswerEnum>(
        WaitingForSecondAnswerEnum,
        urlQueries[ReviewsFilterFieldNameEnum.waitingForSecondAnswer],
        WaitingForSecondAnswerEnum.noSelected
    );
    const reviewModerated: ModeratedEnum = getEnumValueEnsure<ModeratedEnum>(
        ModeratedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.reviewModerated],
        ModeratedEnum.noSelected
    );
    const replyPublished: ReplyPublishedEnum = getEnumValueEnsure<ReplyPublishedEnum>(
        ReplyPublishedEnum,
        urlQueries[ReviewsFilterFieldNameEnum.replyPublished],
        ReplyPublishedEnum.noSelected
    );

    const dateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.dateFrom];
    const dateFrom: Date | null = dateFromRaw ? new Date(dateFromRaw) : null;
    const dateToRaw = urlQueries[ReviewsFilterFieldNameEnum.dateTo];
    const dateTo: Date | null = dateToRaw ? new Date(dateToRaw) : null;

    const collectionDateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.collectionDateFrom];
    const collectionDateFrom: Date | null = collectionDateFromRaw ? new Date(collectionDateFromRaw) : null;
    const collectionDateToRaw = urlQueries[ReviewsFilterFieldNameEnum.collectionDateTo];
    const collectionDateTo: Date | null = collectionDateToRaw ? new Date(collectionDateToRaw) : null;

    const replyDateFromRaw = urlQueries[ReviewsFilterFieldNameEnum.replyDateFrom];
    const replyDateFrom: Date | null = replyDateFromRaw ? new Date(replyDateFromRaw) : null;
    const replyDateToRaw = urlQueries[ReviewsFilterFieldNameEnum.replyDateTo];
    const replyDateTo: Date | null = replyDateToRaw ? new Date(replyDateToRaw) : null;

    const brandIdsRaw = urlQueries[ReviewsFilterFieldNameEnum.brandIds];
    const brandIds: number | null = typeof brandIdsRaw === 'string' ? Number(brandIdsRaw) : null;

    return {
        [ReviewsFilterFieldNameEnum.brandIds]: brandIds,
        [ReviewsFilterFieldNameEnum.sourceList]: sourceList,
        [ReviewsFilterFieldNameEnum.processStatus]: statusList,
        [ReviewsFilterFieldNameEnum.customProcessStatus]: customStatusList,
        [ReviewsFilterFieldNameEnum.ratingList]: ratingList,
        [ReviewsFilterFieldNameEnum.employeesResponsible]: employeeResponsible,
        [ReviewsFilterFieldNameEnum.employeesResponding]: employeeResponding,
        [ReviewsFilterFieldNameEnum.doctorNames]: doctors,
        [ReviewsFilterFieldNameEnum.tagList]: tagList,
        [ReviewsFilterFieldNameEnum.withoutTag]: withoutTag,
        [ReviewsFilterFieldNameEnum.hasNotes]: hasNotes,
        [ReviewsFilterFieldNameEnum.hiddenReviews]: hiddenReviews,

        [ReviewsFilterFieldNameEnum.autoReplay]: autoReplay,
        [ReviewsFilterFieldNameEnum.contentOfReviews]: contentOfReviews,
        [ReviewsFilterFieldNameEnum.deletedReviews]: deletedReviews,
        [ReviewsFilterFieldNameEnum.repliesToReviews]: repliesToReviews,
        [ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: possibilityOfAnAnswer,
        [ReviewsFilterFieldNameEnum.reviewType]: reviewType,
        [ReviewsFilterFieldNameEnum.abuseStatus]: abuseStatus,
        [ReviewsFilterFieldNameEnum.autoAbuse]: autoAbuse,
        [ReviewsFilterFieldNameEnum.reviewChanged]: reviewChanged,
        [ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: waitingForSecondAnswer,
        [ReviewsFilterFieldNameEnum.reviewModerated]: reviewModerated,
        [ReviewsFilterFieldNameEnum.replyPublished]: replyPublished,

        [ReviewsFilterFieldNameEnum.dateFrom]: dateFrom,
        [ReviewsFilterFieldNameEnum.dateTo]: dateTo,
        [ReviewsFilterFieldNameEnum.collectionDateFrom]: collectionDateFrom,
        [ReviewsFilterFieldNameEnum.collectionDateTo]: collectionDateTo,
        [ReviewsFilterFieldNameEnum.replyDateFrom]: replyDateFrom,
        [ReviewsFilterFieldNameEnum.replyDateTo]: replyDateTo,
        [ReviewsFilterFieldNameEnum.agencyRepliers]: agencyRepliers,
    };
}
